/**
 * Runtime polyfills for ES2021 APIs missing on older browsers still in our user
 * base — notably Chrome / Android WebView 80-84, which crash on `Promise.any`
 * and `String.prototype.replaceAll`.
 *
 * Each polyfill installs ONLY when the native implementation is absent, so
 * modern browsers keep their built-in behavior. This module is imported first
 * in main.js so it runs before any feature-using module is evaluated.
 *
 * Note: syntax-level features (e.g. the `??=` operator) cannot be polyfilled at
 * runtime and must be written in an older-syntax form at the source instead.
 */

// AggregateError — added natively in Chrome 85. Required both by the Promise.any
// polyfill below and by any `instanceof AggregateError` check, which would throw
// a ReferenceError on browsers where the global is undefined.
if (typeof globalThis.AggregateError === "undefined") {
  class AggregateErrorPolyfill extends Error {
    readonly errors: unknown[]

    constructor(errors: Iterable<unknown>, message?: string) {
      super(message)
      this.name = "AggregateError"
      this.errors = [...errors]
    }
  }

  // The polyfill matches the constructor (new) usage we rely on; the rarely used
  // call-without-new signature is intentionally not reproduced.
  globalThis.AggregateError =
    AggregateErrorPolyfill as unknown as AggregateErrorConstructor
}

// Promise.any — added natively in Chrome 85. Resolves with the first fulfilled
// value and rejects with an AggregateError only when every input rejects. It
// attaches a handler to every input promise, so a losing promise's rejection
// never surfaces as an unhandled rejection.
if (typeof Promise.any !== "function") {
  const anyPolyfill = <T>(values: Iterable<T | PromiseLike<T>>): Promise<Awaited<T>> =>
    new Promise<Awaited<T>>((resolve, reject) => {
      const items = [...values]
      const errors: unknown[] = []
      let rejectedCount = 0

      if (items.length === 0) {
        reject(new AggregateError([], "All promises were rejected"))
        return
      }

      items.forEach((item, index) => {
        Promise.resolve(item).then(resolve, (error: unknown) => {
          errors[index] = error
          rejectedCount += 1
          if (rejectedCount === items.length) {
            reject(new AggregateError(errors, "All promises were rejected"))
          }
        })
      })
    })

  Promise.any = anyPolyfill
}

// String.prototype.replaceAll — added natively in Chrome 85. For a RegExp it
// defers to `replace` (the spec requires a global RegExp here, so replace
// already covers every match); for a string it builds an equivalent global
// RegExp so string/function replacers behave exactly like the native method.
if (typeof String.prototype.replaceAll !== "function") {
  const escapeRegExp = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

  // eslint-disable-next-line no-extend-native
  String.prototype.replaceAll = function replaceAll(
    this: string,
    searchValue: string | RegExp,
    replaceValue: string | ((substring: string, ...args: unknown[]) => string)
  ): string {
    const pattern =
      searchValue instanceof RegExp
        ? searchValue
        : new RegExp(escapeRegExp(searchValue), "g")

    return typeof replaceValue === "function"
      ? this.replace(pattern, replaceValue)
      : this.replace(pattern, replaceValue)
  }
}
